import React, { useMemo, useState } from "react";
import { useDeviceProfile } from "../../hooks/useDeviceProfile";
import { SmartLink } from "./SmartLink";
import { Modal } from "./Modal";

function extractYouTube(text: string): { id: string; url: string } | null {
  const urls = [...text.matchAll(/https?:\/\/[^\s)]+/gi)].map((m) => m[0]);
  for (const url of urls) {
    try {
      const u = new URL(url);
      if (/^(www\.)?youtube\.com$/i.test(u.hostname)) {
        const id = u.searchParams.get("v");
        if (id) return { id, url };
      }
      if (/^youtu\.be$/i.test(u.hostname)) {
        const id = u.pathname.split("/").filter(Boolean)[0];
        if (id) return { id, url };
      }
    } catch {}
  }
  return null;
}

export function YouTubeSmartPreview({
  text,
  behavior = "auto", // "auto" | "modal" | "newtab"
}: {
  text: string;
  behavior?: "auto" | "modal" | "newtab";
}) {
  // Always call hooks first
  const hit = useMemo(() => extractYouTube(text), [text]);
  const { openStrategy } = useDeviceProfile();
  const [open, setOpen] = useState(false);

  // 👇 Guard comes AFTER hooks
  if (
    text.startsWith("_DEBUG") ||
    text.startsWith("_ActivitySteps") ||
    text.startsWith("_nextStepText")
  ) {
    return null;
  }

  if (!hit) return null;

  const chosen = behavior === "auto" ? openStrategy : behavior;
  const thumb = `https://img.youtube.com/vi/${hit.id}/hqdefault.jpg`;
  const embed = `https://www.youtube-nocookie.com/embed/${hit.id}`;

  const card = (
    <div className="rounded-2xl overflow-hidden border shadow-sm hover:shadow-md transition-shadow">
      <div className="relative w-full aspect-video bg-black/5">
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src={thumb} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="rounded-full px-3 py-1 text-xs bg-black/60 text-white opacity-90">
            ▶ Watch
          </div>
        </div>
      </div>
      <div className="p-3 text-sm opacity-70">YouTube Video</div>
    </div>
  );

  if (chosen === "newtab") {
    return (
      <SmartLink href={hit.url} className="block group">
        {card}
      </SmartLink>
    );
  }

  // chosen === "modal"
  return (
    <>
      <button
        type="button"
        className="block w-full text-left group"
        onClick={() => setOpen(true)}
      >
        {card}
      </button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <div className="relative w-full aspect-video bg-black">
          <iframe
            className="w-full h-full"
            src={embed}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="p-3 flex items-center justify-between">
          <a
            href={hit.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Open in YouTube
          </a>
          <button
            type="button"
            className="px-3 py-1 rounded-lg border"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
}
